/**
 * useTreeLayoutV2.ts — Variable-size-aware Sosa-layout (achter `export.pipeline=v2` flag).
 *
 * Implementeert spec §5.1 (M1-fix). Default-pipeline blijft `legacy` zodat
 * productie ongewijzigd is — V2 wordt geactiveerd door `useTreeLayout`
 * orchestrator wanneer feature-flag op `v2` staat.
 *
 * Refs:
 * - docs/superpowers/plans/2026-05-18-export-layout-redesign.md Fase 5
 * - docs/superpowers/specs/2026-05-18-export-layout-redesign-design.md §5.1
 */

import { useMemo } from 'react';
import type {
  Person,
  Relationship,
  LayoutNode,
  LayoutLink,
  TreeViewMode,
  CardLevel,
  GenealogyEvent,
} from '../types.ts';
import { InvalidProbandError, TreeTooLargeError } from '../utils/treePdfTypes.ts';
import {
  MAX_NODES,
  REDACTED_PLACEHOLDER_WIDTH,
  MIN_SIBLING_GAP_REG,
  computeCardWidthForLevel,
  computePairWidthByGen,
} from '../utils/treePdfShared.ts';
import { assignCardLevel, computeTreeLayoutPureV1 } from './useTreeLayout.ts';
import { pakSosaRooster } from './treeScreenPartnerSpacing.ts';

// Re-export voor backwards-compat: `computeCardWidthForLevel` woont nu in
// `treePdfShared.ts` (zodat V1 hem ook kan gebruiken zonder circulaire import),
// maar bestaande callers/tests importeren hem nog vanuit deze module.
export { computeCardWidthForLevel };

/**
 * Bereken de maximale kaart-breedte per generatie op basis van het toegewezen
 * cardLevel.
 *
 * Spec §5.1 (M1-fix): variable-size-aware layout vereist deze tabel om de
 * top-gen slotPitch te berekenen die geometrisch garandeert dat geen overlap
 * mogelijk is in lagere generaties.
 */
export function computeMaxCardWidthByGen(
  nodes: LayoutNode[],
  cardLevelByGen: Record<number, CardLevel>,
  selectedFields: string[]
): Record<number, number> {
  const result: Record<number, number> = {};
  for (const node of nodes) {
    const lvl = cardLevelByGen[node.generation] ?? node.cardLevel ?? 'detailed';
    const w = computeCardWidthForLevel(lvl, selectedFields);
    result[node.generation] = Math.max(result[node.generation] ?? 0, w);
  }
  return result;
}

/**
 * Bereken slotPitch voor de top-generatie.
 *
 * **CORE M1-FIX**: één pitch voor maxGen, alle lagere lagen volgen
 * geometrisch. Effectieve afstand op gen N = `slotPitch × 2^(maxGen-N)`,
 * wat altijd ≥ `maxCardWidth × 2^(maxGen-N) ≥ maxCardWidth` is.
 *
 * **Edge-case**: lege `maxWidthByGen` retourneert `-Infinity + minGap`. De
 * caller (layoutPedigree) garandeert dat dit pad alleen wordt geraakt na
 * een non-empty nodes-collectie.
 */
export function computeTopGenSlotPitch(
  maxWidthByGen: Record<number, number>,
  minGap: number
): number {
  const values = Object.values(maxWidthByGen);
  const maxCardWidth = Math.max(...values);
  return maxCardWidth + minGap;
}

/**
 * Voeg een pseudo-LayoutNode toe wanneer slechts één van de twee ouders bekend
 * is. Garandeert dat de midpoint-formule (`x = (father.x + mother.x) / 2`) in
 * lagere generaties symmetrisch werkt.
 *
 * Spec §5.1 (E4-fix). Pseudo-id = `pseudo-<sosa>`; krijgt later
 * `__pdfW = REDACTED_PLACEHOLDER_WIDTH` zodat measurement consistent is.
 */
export function ensureBothParentsForLayout(sosa: number, sosaToId: Map<number, string>): void {
  const fatherSosa = sosa * 2;
  const motherSosa = sosa * 2 + 1;
  const hasFather = sosaToId.has(fatherSosa);
  const hasMother = sosaToId.has(motherSosa);
  if (hasFather && !hasMother) {
    sosaToId.set(motherSosa, `pseudo-${motherSosa}`);
  } else if (!hasFather && hasMother) {
    sosaToId.set(fatherSosa, `pseudo-${fatherSosa}`);
  }
  // Beide bekend of beide onbekend: niets doen.
}

/**
 * Maak een pseudo-LayoutNode voor een ontbrekende ouder. Pseudo-nodes worden
 * NIET in de finale layout gerenderd, maar dienen als placeholder voor de
 * midpoint-berekening van het kind. Krijgt `__pdfW = REDACTED_PLACEHOLDER_WIDTH`
 * zodat measurement consistent is in downstream compactify-stappen.
 */
export function makePseudoNode(sosa: number, generation: number): LayoutNode {
  const id = `pseudo-${sosa}`;
  // Minimal Person-shape (geen weergavenaam — wordt niet gerenderd)
  const person: Person = {
    id,
    projectId: '',
    givenNames: '',
    surname: '',
    gender: 'U',
    isLiving: false,
  };
  const node: LayoutNode = {
    person,
    x: 0,
    y: 0,
    generation,
    sosaNumber: sosa,
    isEndOfLine: true,
    hasMoreData: false,
    spouses: [],
    nodeWidth: REDACTED_PLACEHOLDER_WIDTH,
  };
  // __pdfW: optionele attribute door treePdfAssembly/compactify gelezen.
  (node as LayoutNode & { __pdfW?: number }).__pdfW = REDACTED_PLACEHOLDER_WIDTH;
  return node;
}

/**
 * Valideer dat de proband bestaat en een weergavenaam heeft.
 *
 * Spec §5.1 (F1-fix). Wordt aangeroepen vóór elke layout-pass. De UI vangt
 * `InvalidProbandError` af en toont een specifieke fout aan de gebruiker.
 */
export function validateProband(probandId: string | null | undefined, people: Person[]): Person {
  if (!probandId) {
    throw new InvalidProbandError('Geen proband gekozen voor export');
  }
  const proband = people.find((p) => p.id === probandId);
  if (!proband) {
    throw new InvalidProbandError(`Proband id "${probandId}" bestaat niet`);
  }
  const hasGiven = proband.givenNames?.trim();
  const hasSurname = proband.surname?.trim();
  if (!hasGiven && !hasSurname) {
    throw new InvalidProbandError('Proband heeft geen weergavenaam — vul givenNames of surname in');
  }
  return proband;
}

/**
 * Hard cap voor performance.
 *
 * Spec §5.1 (E1+E6-fix). Throwt `TreeTooLargeError` bij meer dan
 * `MAX_NODES = 2048` (~10 generaties pedigree). UI vangt deze error af en
 * toont een modal met de keuze "Verklein generationDepth" of "Annuleer".
 * #1606/#1609 review — telt de UITGELEGDE boom (layout-nodes), niet het hele project:
 * een genealoog heeft al zijn klanten in één project van duizenden personen.
 */
export function assertMaxNodes(nodeCount: number, maxNodes: number = MAX_NODES): void {
  if (nodeCount > maxNodes) {
    throw new TreeTooLargeError(nodeCount, maxNodes);
  }
}

/**
 * Wijs Sosa-nummers toe aan twee ouders op basis van de basis-Sosa.
 *
 * Spec §5.1 (A7-fix + E5-fix):
 * - Standaard `M/F`: vader = `2N`, moeder = `2N+1`
 * - Same-sex `M/M` of `F/F`: oudere ouder krijgt vader-slot (even Sosa)
 * - Onbekende leeftijd → lexicografische id-sortering (deterministisch)
 *
 * Pure function (geen Math.random/Date.now): zelfde input → zelfde output,
 * byte-identical SVG-output gegarandeerd.
 */
export function assignParentSosa(
  parent1: Person,
  parent2: Person,
  baseSosa: number
): { p1Sosa: number; p2Sosa: number } {
  const fatherSosa = baseSosa * 2;
  const motherSosa = baseSosa * 2 + 1;

  // Standaard M/F-conventie
  if (parent1.gender === 'M' && parent2.gender === 'F') {
    return { p1Sosa: fatherSosa, p2Sosa: motherSosa };
  }
  if (parent1.gender === 'F' && parent2.gender === 'M') {
    return { p1Sosa: motherSosa, p2Sosa: fatherSosa };
  }

  // Same-sex of onbekende gender: fallback op geboortejaar (oudere = even/vader-slot)
  const p1Year = extractBirthYear(parent1);
  const p2Year = extractBirthYear(parent2);
  if (p1Year !== p2Year) {
    return p1Year < p2Year
      ? { p1Sosa: fatherSosa, p2Sosa: motherSosa }
      : { p1Sosa: motherSosa, p2Sosa: fatherSosa };
  }

  // Final fallback: lexicografische id-sortering (E5-fix: deterministisch)
  return parent1.id < parent2.id
    ? { p1Sosa: fatherSosa, p2Sosa: motherSosa }
    : { p1Sosa: motherSosa, p2Sosa: fatherSosa };
}

/** Helper: extraheer geboorte-jaar uit Person.birthDate. Onbekend = Infinity. */
function extractBirthYear(p: Person): number {
  const m = p.birthDate?.match(/\d{4}/);
  return m ? parseInt(m[0], 10) : Infinity;
}

/**
 * #1802 — plek in het rooster per voorouder: het Sosa-nummer t.o.v. de FOCUS (focus = 1,
 * diens vader 2, …). Alleen voor de x-berekening: `node.sosaNumber` is proband-relatief
 * (NC #717, staat op de kaart, IMMUTABLE) en blijft ongemoeid, terwijl `generation`
 * focus-relatief is. Is de focus een voorouder met Sosa F, dan heeft zijn voorouder op
 * generatie g het nummer F·2^g + k en hier 2^g + k. Bij focus = proband (F = 1) is dat
 * het eigen Sosa-nummer, dus daar verandert niets.
 *
 * Implex (genummerd via een kortere andere tak, F klopt niet): via V1's ouder-kindlijn
 * (bron = kind, doel = ouder), even/oneven van het eigen nummer = vader/moeder.
 * Geen plek: nakomelingen (generatie < 0) en partners op de focusrij of naast een
 * voorouder, ook als ze zelf een Sosa-nummer dragen (de moeder naast vader-focus).
 */
function roosterSosa(layout: {
  nodes: LayoutNode[];
  links: LayoutLink[];
}): Map<LayoutNode, number> {
  const voorouders = layout.nodes.filter(
    (n) => typeof n.sosaNumber === 'number' && n.sosaNumber >= 1 && n.generation >= 1
  );
  // F = de stam waar de meeste voorouders van afstammen: de focus zelf, of bij een focus
  // buiten de Sosa-lijn (oom) diens broer of zus in die lijn.
  const telling = new Map<number, number>();
  for (const n of voorouders) {
    const f = Math.floor(n.sosaNumber! / 2 ** n.generation);
    telling.set(f, (telling.get(f) ?? 0) + 1);
  }
  let F = 1;
  let meeste = 0;
  for (const [f, c] of telling) {
    if (c > meeste || (c === meeste && f < F)) [F, meeste] = [f, c];
  }

  const plek = new Map<LayoutNode, number>();
  const bezet = new Set<number>();
  const zet = (n: LayoutNode, s: number) => {
    plek.set(n, s);
    bezet.add(s);
  };
  const focus = layout.nodes.find((n) => n.generation === 0 && n.sosaNumber === F);
  if (focus) zet(focus, 1);
  for (const n of voorouders) {
    const top = 2 ** n.generation;
    if (Math.floor(n.sosaNumber! / top) === F) zet(n, top + (n.sosaNumber! % top));
  }

  const nodeById = new Map(layout.nodes.map((n) => [n.person.id, n]));
  const rest = voorouders.filter((n) => !plek.has(n)).sort((a, b) => a.generation - b.generation);
  for (const n of rest) {
    for (const l of layout.links) {
      if (l.type !== 'parent-child' || l.targetId !== n.person.id) continue;
      const kind = nodeById.get(l.sourceId);
      const k = kind && kind.generation === n.generation - 1 ? plek.get(kind) : undefined;
      if (k === undefined) continue;
      const s = 2 * k + (n.sosaNumber! % 2);
      const vrij = bezet.has(s) ? s ^ 1 : s; // onbekend geslacht: het andere slot
      if (!bezet.has(vrij)) zet(n, vrij);
      break;
    }
  }
  return plek;
}

// ─── Pure V2 layout helper (Task 5.6b) ──────────────────────────────────────

/**
 * V2 pure layout — voert eerst de V1-helper uit voor structurele berekening
 * (subtree-walks, spouse-attachment, descendants, cycle-detection) en
 * **HERSCHRIJFT** daarna de x-coordinaten van Sosa-genummerde ancestors
 * volgens de M1-fix formule:
 *
 *   slotPitch[maxGen] = max_g(maxCardWidth[g]) + minGap
 *   topGen.x[slot=i]  = (i - totalSlots/2 + 0.5) * slotPitch[maxGen]
 *   lowerGen.x        = (father.x + mother.x) / 2
 *
 * Pseudo-slots: voor elke Sosa-genummerde node propageert het algoritme
 * een placeholder-x in `xBySosa` voor ontbrekende parent-paren zodat het
 * midpoint-pad correct werkt voor "half-bekende" generaties.
 *
 * Non-PEDIGREE views (DESCENDANTS / PATRILINEAL / MATRILINEAL) lopen de
 * V1 pure helper ongewijzigd: de V2-rewrite raakt alleen Sosa-pedigrees.
 *
 * Pure function: geen `useMemo`, geen side-effects op input-arrays. Returns
 * dezelfde shape als V1 zodat de orchestrator-switch transparent is.
 */
export function computeTreeLayoutPureV2(
  people: Person[],
  relationships: Relationship[],
  focusPersonId: string,
  viewMode: TreeViewMode,
  depthLimit: number,
  isEditMode: boolean = false,
  rootId?: string,
  showSpouses: boolean = true,
  collapsedPartnerNodes?: Set<string>,
  descendantDepth: number = 0,
  events: GenealogyEvent[] = [],
  relationLines: boolean = false,
  partnerGap?: number,
  // Infinity: geen grens (het exportvenster; de keuze voor een grote plaat is aan de gebruiker).
  maxNodes: number = MAX_NODES
): {
  nodes: LayoutNode[];
  links: LayoutLink[];
  dataMaxGeneration: number;
  cardWidthByGen?: Record<number, number>;
} {
  // #1019 — NIET throwen op een (transient) ontbrekende proband tijdens de
  // SCHERM-render. De orchestrator (useTreeLayout) draait useTreeLayoutV2 op ELKE
  // render — óók met de default `legacy`-flag, voor stabiele hook-volgorde — dus
  // een throw hier crasht de hele stamboom-pagina. Dat gebeurt o.a. bij een
  // project-wissel: focusPersonId/rootId komt nog van het vorige project terwijl
  // de nieuwe `people` al geladen zijn. Degradeer dan netjes naar een lege layout,
  // net als computeTreeLayoutPureV1. De EXPORT valideert de proband apart in
  // runExportPipelineV2 (fail-fast met error-banner), dus dat pad blijft streng.
  if (!focusPersonId || !people.some((p) => p.id === focusPersonId)) {
    return { nodes: [], links: [], dataMaxGeneration: 0 };
  }

  // 1) V1-pure call voor structurele basis (handhaaft spouses, descendants, etc.)
  const layout = computeTreeLayoutPureV1(
    people,
    relationships,
    focusPersonId,
    viewMode,
    depthLimit,
    isEditMode,
    rootId,
    showSpouses,
    collapsedPartnerNodes,
    descendantDepth,
    events,
    // #1609 — partnerFootprint: ruimte voor álle partners, zoals het scherm (tree.partnerSpacing).
    // Werkt alleen in DESCENDANTS; het rapportpad (partnerGap) deed dit al.
    true,
    relationLines, // #1089: thread the relation-type-lines flag into export layout
    false, // editPlaceholders
    false, // implex
    partnerGap
  );

  if (layout.nodes.length === 0) return layout;
  // Hard cap (te grote bomen → TreeTooLargeError), op wat er werkelijk uitgelegd is.
  // De V1-layout zelf draait het scherm ook op het hele project; de grens beschermt
  // de dure stappen hierna en in de PDF-pipeline (tekstmeting per kaart).
  assertMaxNodes(layout.nodes.length, maxNodes);

  // 2) Annoteer card-levels (idempotent).
  for (const node of layout.nodes) {
    if (!node.cardLevel) node.cardLevel = assignCardLevel(node.generation);
  }

  // 3) Voor non-PEDIGREE: geen rewrite. V1-layout is al de juiste.
  if (viewMode !== 'PEDIGREE') return layout;

  // 4) PEDIGREE-x-rewrite met slotPitch[maxGen].
  // #1802 — rooster op Sosa t.o.v. de focus (roosterSosa), niet op node.sosaNumber.
  const plek = roosterSosa(layout);
  const sosaNodes = [...plek.keys()];
  if (sosaNodes.length === 0) return layout;

  // Bouw sosa → node mapping
  const nodeBySosa = new Map<number, LayoutNode>();
  for (const n of sosaNodes) {
    nodeBySosa.set(plek.get(n)!, n);
  }

  // Bepaal werkelijke max-generation van Sosa-set (ancestors only, sosaNumber > 0)
  let maxGen = 0;
  for (const n of sosaNodes) {
    if (n.generation > maxGen) maxGen = n.generation;
  }
  if (maxGen === 0) {
    // Alleen proband (sosa=1): niets te herpositioneren. V1 heeft 'm al op (0,0).
    return layout;
  }

  // Bereken slotPitch[maxGen]: max cardWidth over alle gens + MIN_SIBLING_GAP_REG.
  const cardLevelByGen: Record<number, CardLevel> = {};
  for (let g = 0; g <= maxGen; g++) cardLevelByGen[g] = assignCardLevel(g);
  const maxWidthByGen = computeMaxCardWidthByGen(sosaNodes, cardLevelByGen, []);
  // Defensieve fallback: als er voor sommige gens geen waarde is (sparse), vul met
  // de card-width van de toegewezen level zodat Math.max nooit -Infinity raakt.
  for (let g = 0; g <= maxGen; g++) {
    if (maxWidthByGen[g] === undefined) {
      maxWidthByGen[g] = computeCardWidthForLevel(cardLevelByGen[g], []);
    }
  }
  const slotPitch = computeTopGenSlotPitch(maxWidthByGen, MIN_SIBLING_GAP_REG);

  // Stap A: pair-width = child-width clustering (spec §4) — ALLEEN voor 1-5 gen.
  // #525: pair-clustering geldt voor alle depths vanaf 4 zichtbare gens.
  // Top-gen wordt altijd 40×92 rotated; cascade bouwt bottom-up. Voor 6+ gen
  // kan de onderste rij A4-landscape overschrijden — wordt opgelost via tiling
  // (Plan E, follow-up).
  const displayMaxGen = maxGen + 1;
  const usePairClustering = displayMaxGen >= 4;

  let cardWidthByGen: Record<number, number> | undefined;
  let xBySosa: Map<number, number>;

  if (usePairClustering) {
    const pairMap = computePairWidthByGen(displayMaxGen);
    cardWidthByGen = pairMap.cardWidthByGen;
    const intraPairGapByGen = pairMap.intraPairGapByGen;

    const cardWidthTop = cardWidthByGen[displayMaxGen];
    const intraPairDistanceTop = cardWidthTop + intraPairGapByGen[displayMaxGen];
    const pairWidth = cardWidthTop * 2 + intraPairGapByGen[displayMaxGen];
    // #1005: de gedraaide toprij heeft smalle 90px-kaarten — de royale 50px
    // inter-pair gap maakt de boom onnodig breed (op A4 is de boom breedte-
    // gelimiteerd, niet hoogte). Gebruik een krappe gap voor de rotated toprij,
    // met een vloer = breedte van de kaart één generatie lager zodat die rij
    // niet gaat overlappen.
    const belowWidth = cardWidthByGen[displayMaxGen - 1] ?? cardWidthTop;
    const interPairGap = pairMap.rotateTopGen ? 14 : MIN_SIBLING_GAP_REG;
    const pairPitch = Math.max(pairWidth + interPairGap, belowWidth + 14);
    const totalTopSlots = Math.pow(2, maxGen);
    const pairCount = totalTopSlots / 2;
    xBySosa = new Map<number, number>();
    for (let p = 0; p < pairCount; p++) {
      const pairCenter = (p - pairCount / 2 + 0.5) * pairPitch;
      const fatherSosa = Math.pow(2, maxGen) + p * 2;
      const motherSosa = fatherSosa + 1;
      xBySosa.set(fatherSosa, pairCenter - intraPairDistanceTop / 2);
      xBySosa.set(motherSosa, pairCenter + intraPairDistanceTop / 2);
    }
  } else {
    // Legacy slotPitch math voor 6+ gen (tot tiling wordt geïmplementeerd).
    // Identiek aan de pre-Task-2 implementatie.
    const totalTopSlots = Math.pow(2, maxGen);
    xBySosa = new Map<number, number>();
    for (let i = 0; i < totalTopSlots; i++) {
      const sosa = Math.pow(2, maxGen) + i;
      const x = (i - totalTopSlots / 2 + 0.5) * slotPitch;
      xBySosa.set(sosa, x);
    }
  }
  void slotPitch; // legacy variable nog door downstream gelezen

  // Stap B: lagere generaties top-down via midpoint(father, mother).
  // Pseudo-slots ontstaan automatisch omdat we voor élk slot een x berekenen,
  // ook al heeft de Sosa geen werkelijke node in sosaNodes.
  for (let gen = maxGen - 1; gen >= 0; gen--) {
    const slotsInGen = Math.pow(2, gen);
    for (let i = 0; i < slotsInGen; i++) {
      const sosa = Math.pow(2, gen) + i;
      const fatherSosa = sosa * 2;
      const motherSosa = sosa * 2 + 1;
      const fx = xBySosa.get(fatherSosa);
      const mx = xBySosa.get(motherSosa);
      if (fx !== undefined && mx !== undefined) {
        xBySosa.set(sosa, (fx + mx) / 2);
      } else if (fx !== undefined) {
        xBySosa.set(sosa, fx);
      } else if (mx !== undefined) {
        xBySosa.set(sosa, mx);
      }
      // else: geen kinderen onder dit slot — niets toe te wijzen.
    }
  }

  // Stap C: schrijf nieuwe x naar Sosa-genummerde nodes en re-center op proband.
  const probandNode = nodeBySosa.get(1);
  const probandX = xBySosa.get(1) ?? 0;
  // Bereken offset zodat proband op x=0 staat (matcht V1's centerOnProband).
  const offsetX = probandX;

  // Bewaar oude x-waarden om non-Sosa nodes (spouses, descendants) consistent
  // te kunnen meeshiften. We schuiven niet-sosa nodes met hetzelfde delta dat
  // hun "ankerpunt" (eerstvolgende Sosa-node op zelfde generatie) heeft.
  const oldXById = new Map<string, number>();
  for (const n of layout.nodes) oldXById.set(n.person.id, n.x);

  // Bouw delta per persoon-id: alleen Sosa-genummerde nodes krijgen een
  // nieuwe x; spouses + descendant-nodes verschuiven mee volgens de delta
  // van hun "primaire" Sosa-node (de bloodline-partner of -ouder).
  for (const n of sosaNodes) {
    const newX = xBySosa.get(plek.get(n)!);
    if (newX === undefined) continue;
    n.x = newX - offsetX;
  }

  // Stap D: spouses + descendants meeschuiven. #1005 fix: schuif elke non-sosa
  // node mee met de delta van de DICHTSTBIJZIJNDE Sosa-node op dezelfde generatie
  // (= de voorouder waar 'ie in de V1-layout naast stond), niet met de proband-
  // delta. Anders kreeg een spouse van een diepe voorouder de verkeerde delta en
  // belandde 'ie als uitschieter ver weg (overlap). Fallback: proband-delta.
  const oldProbandX = probandNode ? (oldXById.get(probandNode.person.id) ?? 0) : 0;
  const probandDelta = probandNode ? probandNode.x - oldProbandX : 0;

  // Per generatie: lijst van Sosa-ankers met hun oude x + delta.
  const sosaAnchorsByGen = new Map<number, Array<{ oldX: number; delta: number }>>();
  for (const n of sosaNodes) {
    const newX = xBySosa.get(plek.get(n)!);
    if (newX === undefined) continue;
    const oldX = oldXById.get(n.person.id) ?? n.x;
    const delta = newX - offsetX - oldX;
    const arr = sosaAnchorsByGen.get(n.generation) ?? [];
    arr.push({ oldX, delta });
    sosaAnchorsByGen.set(n.generation, arr);
  }

  for (const n of layout.nodes) {
    if (plek.has(n)) continue; // al gedaan
    const oldX = oldXById.get(n.person.id) ?? n.x;
    const anchors = sosaAnchorsByGen.get(n.generation);
    if (anchors && anchors.length > 0) {
      let best = anchors[0];
      for (const a of anchors) {
        if (Math.abs(a.oldX - oldX) < Math.abs(best.oldX - oldX)) best = a;
      }
      n.x = oldX + best.delta;
    } else {
      // Geen Sosa-anker op deze generatie (bv. descendant-nodes) → proband-delta.
      n.x = oldX + probandDelta;
    }
  }

  // #1793 — de focusrij met twee of meer partners houdt V1's volgorde (focus in het
  // midden). Het rooster rekent met Sosa-nummers van de proband: is de focus een
  // voorouder (Jan = 2), dan schoof het zijn partner met een eigen Sosa-nummer (Maria = 3)
  // naar het moederslot, bovenop de andere partner (reality-check prod-kopie, 16 okt).
  const focusNode = layout.nodes.find((n) => n.person.id === focusPersonId && n.generation === 0);
  const focusRij = layout.nodes.filter((n) => n !== focusNode && n.generation === 0);
  if (focusNode && focusRij.length >= 2) {
    const delta = focusNode.x - (oldXById.get(focusPersonId) ?? focusNode.x);
    for (const n of focusRij) n.x = (oldXById.get(n.person.id) ?? n.x) + delta;
  }

  // Stap E: re-sync link-coordinaten (sourceX/targetX) na x-rewrite.
  const xById = new Map<string, number>();
  for (const n of layout.nodes) xById.set(n.person.id, n.x);
  for (const link of layout.links) {
    const sx = xById.get(link.sourceId);
    const tx = xById.get(link.targetId);
    if (sx !== undefined) link.sourceX = sx;
    if (tx !== undefined) link.targetX = tx;
    if (link.childXs !== undefined) {
      link.childXs = link.childXs.map((_, idx) => {
        const childId = link.childIds?.[idx];
        return childId ? (xById.get(childId) ?? link.childXs![idx]) : link.childXs![idx];
      });
    }
  }

  // Stap F: annotate top-gen nodes met rotation flag — alleen bij pair-clustering.
  if (usePairClustering) {
    const pairMap = computePairWidthByGen(displayMaxGen);
    if (pairMap.rotateTopGen) {
      for (const n of layout.nodes) {
        if (n.generation === maxGen) {
          (n as LayoutNode & { __rotated?: boolean }).__rotated = true;
        }
      }
    }
  }

  // Stap G: annotate `__pdfW` per node op basis van cardWidthByGen — alleen bij pair-clustering.
  if (cardWidthByGen) {
    for (const n of layout.nodes) {
      const w = cardWidthByGen[n.generation + 1]; // layout-generation 0 = display-gen 1
      if (w !== undefined) {
        (n as LayoutNode & { __pdfW?: number }).__pdfW = w;
      }
    }
  }

  return { ...layout, cardWidthByGen };
}

/**
 * Bouw een mapping: sosa-node-id → lijst van zijn EXTRA (niet-voorouderlijke)
 * spouse-nodes. Een extra spouse is een non-sosa LayoutNode die via een
 * `spousal`-link aan een sosa-node hangt (2e echtgenoten e.d.). Bloedlijn-paren
 * (waar beide partners zelf sosa-nodes zijn) tellen NIET mee — die staan al als
 * vader/moeder in de kwartierstaat.
 */
function buildExtraSpouseMap(
  layout: {
    nodes: LayoutNode[];
    links: LayoutLink[];
  },
  plek: Map<LayoutNode, number>
): Map<string, LayoutNode[]> {
  const nodeById = new Map<string, LayoutNode>();
  for (const n of layout.nodes) nodeById.set(n.person.id, n);
  const isSosa = (n: LayoutNode) => plek.has(n); // #1802 — een plek in het rooster

  const result = new Map<string, LayoutNode[]>();
  for (const link of layout.links) {
    if (link.type !== 'spousal') continue;
    const a = nodeById.get(link.sourceId);
    const b = nodeById.get(link.targetId);
    if (!a || !b) continue;
    let sosaNode: LayoutNode | undefined;
    let spouse: LayoutNode | undefined;
    if (isSosa(a) && !isSosa(b)) {
      sosaNode = a;
      spouse = b;
    } else if (isSosa(b) && !isSosa(a)) {
      sosaNode = b;
      spouse = a;
    }
    if (!sosaNode || !spouse) continue;
    const arr = result.get(sosaNode.person.id) ?? [];
    if (!arr.some((s) => s.person.id === spouse!.person.id)) arr.push(spouse);
    result.set(sosaNode.person.id, arr);
  }
  return result;
}

/** Aantal extra spouse-nodes voor een gegeven sosa-nummer (0 als geen node/geen extra). */
function extraSpouseCount(
  nodeBySosa: Map<number, LayoutNode>,
  extraSpousesBySosaId: Map<string, LayoutNode[]>,
  sosaNum: number
): number {
  const node = nodeBySosa.get(sosaNum);
  if (!node) return 0;
  return extraSpousesBySosaId.get(node.person.id)?.length ?? 0;
}

/**
 * Pas variable-size-aware top-gen pair-clustering toe op een bestaande V1-layout.
 *
 * Use-case: de V1 → buildPedigreeRootSvg export-pipeline. V1's pair-clustering
 * gebruikt `topDims.width` (= COMPACT_NODE_WIDTH=111 voor gen 4+) wat aansluit
 * bij hoe TreeCanvas in /tree-view rendert. Maar de export-renderer
 * (`buildCardStandard`/`Detailed`/`Compact` in treePdfCards.ts) rendert gen 4-5
 * cards op STANDARD.w=180 en gen 6+ op COMPACT.w=130 — beide breder dan V1's
 * layout-aanname. Resultaat: 59px overlap per paar op gen 4 in de export.
 *
 * Deze helper herpositioneert sosa-nodes met de echte render-breedte van het
 * export-pad, en cascadeert via midpoint-formule naar lagere generaties. Non-
 * sosa nodes (spouses, descendants) volgen via proband-delta.
 *
 * Idempotent: meermaals aanroepen geeft hetzelfde resultaat omdat de positie
 * van scratch wordt berekend uit sosa-nummers.
 */
export function applyVariableSizeAwareSpacing(
  layout: { nodes: LayoutNode[]; links: LayoutLink[] },
  viewMode: TreeViewMode,
  selectedFields: string[] = []
): void {
  if (layout.nodes.length === 0) return;

  // Annoteer card-levels (idempotent).
  for (const node of layout.nodes) {
    if (!node.cardLevel) node.cardLevel = assignCardLevel(node.generation);
  }

  // Non-PEDIGREE: geen rewrite nodig.
  if (viewMode !== 'PEDIGREE') return;

  // #1802 — rooster op Sosa t.o.v. de focus (roosterSosa), niet op node.sosaNumber.
  const plek = roosterSosa(layout);
  const sosaNodes = [...plek.keys()];
  if (sosaNodes.length === 0) return;

  const nodeBySosa = new Map<number, LayoutNode>();
  for (const n of sosaNodes) {
    nodeBySosa.set(plek.get(n)!, n);
  }

  let maxGen = 0;
  for (const n of sosaNodes) {
    if (n.generation > maxGen) maxGen = n.generation;
  }
  if (maxGen === 0) return;

  const cardLevelByGen: Record<number, CardLevel> = {};
  for (let g = 0; g <= maxGen; g++) cardLevelByGen[g] = assignCardLevel(g);

  // #525: pair-clustering moet rotated-widths respecteren. Voor 4+ gen tree
  // gebruikt de top-rij rotated 90×180 cards i.p.v. cardLevel-widths (180/220).
  // Layout-positionering moet ook 90 gebruiken anders ontstaat groot gat tussen
  // father/mother (cards 90 wide maar positions 190 apart = 100px gap).
  const displayMaxGen = maxGen + 1;
  const pairMap = computePairWidthByGen(displayMaxGen);
  const cardWidthTop = pairMap.cardWidthByGen[displayMaxGen];
  const TOP_GEN_SPOUSE_GAP = pairMap.intraPairGapByGen[displayMaxGen];
  // #1005: krappe inter-pair gap voor de gedraaide toprij (smalle 90px-kaarten)
  // zodat de boom niet onnodig breed wordt op A4. De rij eronder houdt 14px
  // tussenruimte (was de `belowWidth + 14`-vloer van de uniforme pitch).
  const interPairGap = pairMap.rotateTopGen ? 14 : MIN_SIBLING_GAP_REG;

  // #1005 (partner-toggle) — extra, niet-voorouderlijke partners (2e echtgenoten)
  // krijgen eigen LayoutNodes (sosaNumber undefined, isSpouseNode) en komen NAAST
  // hun bloedlijn-partner, aan de buitenkant.
  //
  // NC #842 (#1793) — afstand per paar, zelfde principe als het scherm
  // (`pakSosaRooster`): het rooster wordt per generatie op botsingen gepakt en
  // alleen de omgeving van een paar met extra partners schuift op. Vervangt de
  // uniforme pitch die in stappen van 4px groeide tot niets meer botste (#1590) —
  // die maakte élk paar breder. Zonder extra partners exact het oude rooster.
  const SPOUSE_GAP = 12;
  const extraSpousesBySosaId = buildExtraSpouseMap(layout, plek);
  const xBySosa = pakSosaRooster({
    maxGen,
    voetafdruk: (sosa, gen) => {
      const w = pairMap.cardWidthByGen[gen + 1] ?? cardWidthTop;
      const extra = extraSpouseCount(nodeBySosa, extraSpousesBySosaId, sosa) * (w + SPOUSE_GAP);
      return sosa % 2 === 0 ? [w / 2 + extra, w / 2] : [w / 2, w / 2 + extra];
    },
    tussenruimte: (gen, paar) => (gen === maxGen ? (paar ? TOP_GEN_SPOUSE_GAP : interPairGap) : 14),
  });

  // Schrijf nieuwe x naar Sosa-nodes en re-center op proband (proband op x=0).
  const probandNode = nodeBySosa.get(1);
  const probandX = xBySosa.get(1) ?? 0;
  const offsetX = probandX;

  // Bewaar oude x voor non-Sosa delta-berekening.
  const oldXById = new Map<string, number>();
  for (const n of layout.nodes) oldXById.set(n.person.id, n.x);

  for (const n of sosaNodes) {
    const newX = xBySosa.get(plek.get(n)!);
    if (newX === undefined) continue;
    n.x = newX - offsetX;
  }

  // #1005 — plaats extra spouse-nodes NAAST hun bloedlijn-partner (buitenzijde van
  // het paar: vader-partners naar links, moeder-partners naar rechts). De pitch is
  // hierboven al verbreed zodat dit niet met buur-kaarten botst.
  const placedSpouseIds = new Set<string>();
  for (const partner of sosaNodes) {
    const spouses = extraSpousesBySosaId.get(partner.person.id);
    if (!spouses || spouses.length === 0) continue;
    const sosaNum = plek.get(partner)!;
    // #1793 — de focus (generatie 0) met twee of meer partners staat tussen hen in
    // (V1, partnerPlekken), ook als hij een voorouder van de proband is (Sosa ≥ 2);
    // zijn partners schuiven met hem mee (zie treeScreenPartnerSpacing).
    // Hele focusrij: een partner met een eigen Sosa-nummer telt ook mee.
    const focusRij =
      partner.generation === 0
        ? layout.nodes.filter((n) => n !== partner && n.generation === 0)
        : [];
    if (focusRij.length >= 2) {
      const delta = partner.x - (oldXById.get(partner.person.id) ?? partner.x);
      for (const sp of focusRij) {
        sp.x = (oldXById.get(sp.person.id) ?? sp.x) + delta;
        sp.y = partner.y;
        placedSpouseIds.add(sp.person.id);
      }
      continue;
    }
    const isFather = sosaNum % 2 === 0; // even sosa = vader (links), oneven = moeder (rechts)
    const genCardW = pairMap.cardWidthByGen[partner.generation + 1] ?? cardWidthTop;
    const unit = genCardW + SPOUSE_GAP;
    spouses.forEach((sp, idx) => {
      const k = idx + 1;
      sp.x = partner.x + (isFather ? -k * unit : k * unit);
      sp.y = partner.y;
      placedSpouseIds.add(sp.person.id);
    });
  }

  // Overige non-Sosa nodes (bv. afstammelingen) verschuiven met proband-delta.
  if (probandNode) {
    const oldProbandX = oldXById.get(probandNode.person.id) ?? 0;
    const probandDelta = probandNode.x - oldProbandX;
    for (const n of layout.nodes) {
      if (plek.has(n)) continue; // al gedaan
      if (placedSpouseIds.has(n.person.id)) continue; // al naast partner geplaatst
      n.x = (oldXById.get(n.person.id) ?? n.x) + probandDelta;
    }
  }

  // Re-sync link-coordinaten (sourceX/targetX) na x-rewrite.
  const xById = new Map<string, number>();
  for (const n of layout.nodes) xById.set(n.person.id, n.x);
  for (const link of layout.links) {
    const sx = xById.get(link.sourceId);
    const tx = xById.get(link.targetId);
    if (sx !== undefined) link.sourceX = sx;
    if (tx !== undefined) link.targetX = tx;
    if (link.childXs !== undefined) {
      link.childXs = link.childXs.map((_, idx) => {
        const childId = link.childIds?.[idx];
        return childId ? (xById.get(childId) ?? link.childXs![idx]) : link.childXs![idx];
      });
    }
  }
}

// ─── V2 hook (Task 5.6) ─────────────────────────────────────────────────────

/**
 * Variable-size-aware Sosa-layout hook (V2).
 *
 * Spec §5.1 layoutPedigree V2. Signature identiek aan V1 zodat de orchestrator
 * tussen V1 en V2 kan switchen op feature-flag zonder caller-wijzigingen.
 *
 * **Implementatie-strategie**:
 * 1. Hard cap op input-size (E1+E6-fix) — pre-check op `people`-array.
 * 2. Proband-guard (F1-fix) — throws bij invalid/missing proband.
 * 3. Delegeert aan `computeTreeLayoutPureV2` voor de daadwerkelijke layout-
 *    berekening (V1-pure-call + slotPitch[maxGen] x-rewrite voor PEDIGREE).
 *
 * **Determinisme** (E5-fix): alle gebruikte helpers zijn pure functions; geen
 * `Math.random`/`Date.now`. Output is byte-identical bij identieke input.
 */
export function useTreeLayoutV2(
  people: Person[],
  relationships: Relationship[],
  focusPersonId: string,
  viewMode: TreeViewMode,
  depthLimit: number,
  isEditMode: boolean = false,
  rootId?: string,
  showSpouses: boolean = true,
  collapsedPartnerNodes?: Set<string>,
  descendantDepth: number = 0,
  events: GenealogyEvent[] = [],
  enabled: boolean = true
): {
  nodes: LayoutNode[];
  links: LayoutLink[];
  dataMaxGeneration: number;
} {
  return useMemo(
    () =>
      enabled
        ? computeTreeLayoutPureV2(
            people,
            relationships,
            focusPersonId,
            viewMode,
            depthLimit,
            isEditMode,
            rootId,
            showSpouses,
            collapsedPartnerNodes,
            descendantDepth,
            events
          )
        : { nodes: [], links: [], dataMaxGeneration: 0 },
    [
      enabled,
      people,
      relationships,
      focusPersonId,
      viewMode,
      depthLimit,
      isEditMode,
      rootId,
      showSpouses,
      collapsedPartnerNodes,
      descendantDepth,
      events,
    ]
  );
}
